import { FormEvent, FunctionComponent, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useTranslation } from "next-i18next/pages";
import { trackEvent } from "lib/analytics";
import styles from "../../styles/contact/ContactForm.module.css";

type FormStatus = "idle" | "sending" | "success" | "error";

interface ContactValues {
  firstName: string;
  lastName: string;
  email: string;
  topic: string;
  message: string;
  company: string;
}

const INITIAL_VALUES: ContactValues = {
  firstName: "",
  lastName: "",
  email: "",
  topic: "",
  message: "",
  company: ""
};

const ContactForm: FunctionComponent = () => {
  const { t } = useTranslation("contact");
  const [values, setValues] = useState<ContactValues>(INITIAL_VALUES);
  const [status, setStatus] = useState<FormStatus>("idle");
  const formRef = useRef<HTMLFormElement>(null);
  const topicOptions = t("form.topic.options", { returnObjects: true }) as string[];

  useEffect(() => {
    trackEvent("contact-form-view");
  }, []);

  const setValue = (name: keyof ContactValues, value: string) => {
    setValues((current) => ({ ...current, [name]: value }));
    if (status === "error") setStatus("idle");
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!formRef.current?.reportValidity()) return;

    setStatus("sending");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values)
      });

      if (!response.ok) {
        setStatus("error");
        return;
      }

      setStatus("success");
      setValues(INITIAL_VALUES);
      trackEvent("contact-form-submit", { topic: values.topic });
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div className={styles.successCard} role="status">
        <p className={styles.kicker}>{t("form.successKicker")}</p>
        <h2>{t("form.successTitle")}</h2>
        <p>{t("form.successText")}</p>
        <button className={styles.resetButton} type="button" onClick={() => setStatus("idle")}>
          {t("form.successReset")} <span aria-hidden="true">↗</span>
        </button>
      </div>
    );
  }

  return (
    <form id="contact-form" className={styles.form} onSubmit={handleSubmit} ref={formRef}>
      <div className={styles.formIntro}>
        <p className={styles.kicker}>{t("form.kicker")}</p>
        <h2>{t("form.title")}</h2>
        <span>{t("form.intro")}</span>
      </div>

      <div className={styles.nameRow}>
        <label>
          <span>{t("form.firstName")} *</span>
          <input
            type="text"
            name="firstName"
            autoComplete="given-name"
            maxLength={100}
            required
            value={values.firstName}
            onChange={(event) => setValue("firstName", event.target.value)}
          />
        </label>
        <label>
          <span>{t("form.lastName")} *</span>
          <input
            type="text"
            name="lastName"
            autoComplete="family-name"
            maxLength={100}
            required
            value={values.lastName}
            onChange={(event) => setValue("lastName", event.target.value)}
          />
        </label>
      </div>

      <label>
        <span>{t("form.email")} *</span>
        <input
          type="email"
          name="email"
          autoComplete="email"
          inputMode="email"
          maxLength={200}
          required
          value={values.email}
          onChange={(event) => setValue("email", event.target.value)}
        />
      </label>

      <label>
        <span>{t("form.topic.label")} *</span>
        <select
          name="topic"
          required
          value={values.topic}
          onChange={(event) => setValue("topic", event.target.value)}
        >
          <option value="" disabled>{t("form.topic.placeholder")}</option>
          {topicOptions.map((topic) => <option key={topic} value={topic}>{topic}</option>)}
        </select>
      </label>

      <label>
        <span>{t("form.message")} *</span>
        <textarea
          name="message"
          rows={5}
          maxLength={5000}
          required
          value={values.message}
          onChange={(event) => setValue("message", event.target.value)}
        />
      </label>

      <div className={styles.honeypot} aria-hidden="true">
        <label htmlFor="contact-company">Company</label>
        <input
          id="contact-company"
          name="company"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={values.company}
          onChange={(event) => setValue("company", event.target.value)}
        />
      </div>

      {status === "error" && (
        <p className={styles.error} role="alert">{t("form.error")}</p>
      )}

      <button className={styles.submitButton} type="submit" disabled={status === "sending"}>
        <span>{status === "sending" ? t("form.sending") : t("form.submit")}</span>
        <b aria-hidden="true">↗</b>
      </button>

      <p className={styles.privacy}>
        {t("form.privacyPrefix")}{" "}
        <Link href="/datenschutz">{t("form.privacyLink")}</Link>.
      </p>
    </form>
  );
};

export default ContactForm;
